"use client";

import { useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { useLanguage, useTranslation } from "@/hooks/useLanguage";
import { setLanguage } from "@/utils/multilingual";
import { useRouter } from "next/navigation";

const languages = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
];

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const currentLang = useLanguage();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const currentLanguage = languages.find((lang) => lang.code === currentLang);

  const handleLanguageChange = (lang) => {
    if (lang === currentLang) {
      setOpen(false);
      return;
    }

    setIsPending(true);
    setOpen(false);
    setLanguage(lang);
    router.refresh();

    // Reset pending after a short delay to allow refresh
    setTimeout(() => setIsPending(false), 500);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-2 px-3 py-2 hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
          aria-label={t("common.language") || "Language"}
          disabled={isPending}
        >
          <Globe className={`size-4 ${isPending ? "animate-spin" : ""}`} />
          <span className="text-sm font-semibold">
            {currentLanguage?.name}
          </span>
          <ChevronDown className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="end">
        <div className="space-y-1">
          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">
            {t("common.language") || "Language"}
          </div>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              disabled={isPending}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-accent transition-colors text-start disabled:opacity-50"
            >
              <div>
                <div className="text-sm font-medium">{lang.name}</div>
                <div className="text-xs text-muted-foreground">
                  {lang.nativeName}
                </div>
              </div>
              {currentLang === lang.code && (
                <Check className="size-4 text-primary" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
